import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { getPartiesNoOrderSummary, getPartiesUnpaidInvoiceSummary } from '@/app/services/partyService';
import { useAuthStore } from '@/app/store/authStore';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';

/**
 * Severity ramp for an aging bucket.
 *
 * Deliberately runs plain -> hot rather than green -> red: every party on this
 * screen is one that needs chasing, and green would read as "this one is fine".
 * The colour is carried by the number itself plus a rule beneath it, so severity
 * costs no extra element.
 */
type Tone = 'mild' | 'warn' | 'high' | 'urgent';

const TONE_VALUE: Record<Tone, string> = {
    mild: 'text-foreground',
    warn: 'text-amber-400',
    high: 'text-orange-400',
    urgent: 'text-red-400',
};

const TONE_RULE: Record<Tone, string> = {
    mild: 'bg-muted-foreground/25',
    warn: 'bg-amber-400/70',
    high: 'bg-orange-400/70',
    urgent: 'bg-red-400/80',
};

/** The four aging bands both summaries are bucketed into, mildest first. */
const BANDS: Array<{ from: number; to: number; label: string; tone: Tone }> = [
    { from: 15, to: 30, label: '15–29 days', tone: 'mild' },
    { from: 30, to: 60, label: '30–59 days', tone: 'warn' },
    { from: 60, to: 90, label: '60–89 days', tone: 'high' },
    { from: 90, to: 36500, label: '90+ days', tone: 'urgent' },
];

interface LadderCellProps {
    label: string;
    count: number;
    tone: Tone;
    to: string;
}

/**
 * One rung of an aging ladder.
 *
 * The count leads and the band label supports it, so a row of four reads as
 * numbers first. An empty band is dimmed rather than dropped — the gap in the
 * ladder is itself information, and a loud "0" competes with a real 72.
 */
function LadderCell({ label, count, tone, to }: LadderCellProps) {
    const empty = count === 0;

    return (
        <Link
            to={to}
            aria-label={`${count} parties, ${label}`}
            className="relative flex flex-col items-center justify-center gap-1 px-0.5 py-4 active:bg-accent transition-colors"
        >
            <span
                className={cn(
                    'text-[28px] leading-none font-semibold tabular-nums',
                    empty ? 'text-muted-foreground/40' : TONE_VALUE[tone]
                )}
            >
                {count}
            </span>
            <span className="text-[11px] leading-none tracking-tight text-muted-foreground whitespace-nowrap">
                {label}
            </span>
            <span
                className={cn(
                    'absolute inset-x-0 bottom-0 h-[3px]',
                    empty ? 'bg-border' : TONE_RULE[tone]
                )}
            />
        </Link>
    );
}

interface AgingLadderProps {
    title: string;
    /**
     * Parties actually reachable from this ladder — the bands plus any footer row.
     *
     * Deliberately NOT the API's own headline count: `total_parties_with_unpaid`
     * includes parties whose oldest unpaid invoice is under 15 days, which no band
     * on screen covers, so showing it puts a number beside a ladder that cannot add
     * up to it.
     */
    shown: number;
    /** Every party assigned to this manager, as the denominator. */
    total: number;
    buckets: Record<string, number>;
    /** Builds the parties-list URL for one band. */
    hrefFor: (from: number, to: number) => string;
    /** Optional extra row rendered under the ladder, inside the same card. */
    footer?: React.ReactNode;
}

/**
 * One aging ladder: four bands in a single row, divided by hairlines inside one
 * card. A single card rather than four keeps the four bands reading as one
 * instrument, and the escalation left-to-right is visible without reading a label.
 */
function AgingLadder({ title, shown, total, buckets, hrefFor, footer }: AgingLadderProps) {
    return (
        <section>
            <div className="flex items-baseline justify-between mb-2">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {title}
                </h2>
                <span className="text-xs text-muted-foreground tabular-nums">
                    {shown} of {total}
                </span>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="grid grid-cols-4 divide-x divide-border">
                    {BANDS.map((band) => (
                        <LadderCell
                            key={band.from}
                            label={band.label}
                            count={buckets[String(band.from)] || 0}
                            tone={band.tone}
                            to={hrefFor(band.from, band.to)}
                        />
                    ))}
                </div>
                {footer}
            </div>
        </section>
    );
}

interface CardRowProps {
    label: string;
    value: number;
    to: string;
    /** Rows inside a ladder card need the rule above them; standalone ones don't. */
    divided?: boolean;
}

function CardRow({ label, value, to, divided = false }: CardRowProps) {
    return (
        <Link
            to={to}
            className={cn(
                'flex items-center justify-between gap-3 px-4 py-3 active:bg-accent transition-colors',
                divided && 'border-t border-border'
            )}
        >
            <span className="text-sm text-foreground">{label}</span>
            <span className="flex items-center gap-2">
                <span className="text-base font-semibold tabular-nums">{value}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </span>
        </Link>
    );
}

const sumBuckets = (buckets: Record<string, number> | undefined): number =>
    BANDS.reduce((sum, band) => sum + (buckets?.[String(band.from)] || 0), 0);

function DashboardSkeleton() {
    return (
        <div className="p-4 space-y-6">
            {[1, 2].map((section) => (
                <div key={section}>
                    <MobileSkeleton className="h-3 w-32 mb-3" />
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="grid grid-cols-4 divide-x divide-border">
                            {[1, 2, 3, 4].map((i) => (
                                <div
                                    key={i}
                                    className="flex flex-col items-center gap-2 px-1 py-4"
                                >
                                    <MobileSkeleton className="h-7 w-8" />
                                    <MobileSkeleton className="h-2.5 w-14" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function PartyDashboardPage() {
    const user = useAuthStore((state) => state.user);
    const responsiblePartyId = user?.managed_parties?.[0]?.id;
    const hasResponsibleParties = !!responsiblePartyId;

    const { data, isLoading, refetch, isFetching } = useQuery({
        queryKey: ['parties-no-order-summary', responsiblePartyId],
        queryFn: () => getPartiesNoOrderSummary(responsiblePartyId, undefined, true),
        refetchInterval: 60000,
        enabled: hasResponsibleParties,
    });

    const {
        data: unpaidData,
        isLoading: unpaidLoading,
        refetch: unpaidRefetch,
        isFetching: unpaidFetching,
    } = useQuery({
        queryKey: ['parties-unpaid-invoice-summary', responsiblePartyId],
        queryFn: () => getPartiesUnpaidInvoiceSummary(responsiblePartyId),
        refetchInterval: 60000,
        enabled: hasResponsibleParties,
    });

    const refreshing = isFetching || unpaidFetching;
    const asOf = data?.as_of_date || unpaidData?.as_of_date;
    const totalParties = data?.total_parties || unpaidData?.total_parties || 0;

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title="Parties"
                rightAction={
                    hasResponsibleParties ? (
                        <button
                            type="button"
                            aria-label="Refresh"
                            onClick={() => {
                                refetch();
                                unpaidRefetch();
                            }}
                            className="flex items-center justify-center w-10 h-10 rounded-full active:bg-accent"
                            disabled={refreshing}
                        >
                            <RefreshCw
                                className={cn(
                                    'h-5 w-5 text-muted-foreground',
                                    refreshing && 'animate-spin'
                                )}
                            />
                        </button>
                    ) : undefined
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {!hasResponsibleParties ? (
                    <MobileEmptyState
                        icon={<Building2 className="h-12 w-12" />}
                        title="No parties assigned"
                        description="You are not responsible for any parties yet. Ask admin to assign you some."
                    />
                ) : isLoading || unpaidLoading ? (
                    <DashboardSkeleton />
                ) : !data && !unpaidData ? (
                    <MobileEmptyState
                        icon={<Building2 className="h-12 w-12" />}
                        title="Couldn't load dashboard"
                        description="Pull the refresh button up top to try again."
                    />
                ) : (
                    <div className="p-4 space-y-6">
                        {data && (
                            <AgingLadder
                                title="No orders since"
                                shown={sumBuckets(data.buckets) + data.never_ordered}
                                total={data.total_parties}
                                buckets={data.buckets}
                                hrefFor={(from, to) =>
                                    `/parties/list?noOrderDays=${from}&noOrderDaysMax=${to}`
                                }
                                footer={
                                    <CardRow
                                        divided
                                        label="Never ordered"
                                        value={data.never_ordered}
                                        to="/parties/list?neverOrdered=true"
                                    />
                                }
                            />
                        )}

                        {unpaidData && (
                            <AgingLadder
                                title="Unpaid invoices"
                                shown={sumBuckets(unpaidData.buckets)}
                                total={unpaidData.total_parties}
                                buckets={unpaidData.buckets}
                                hrefFor={(from, to) =>
                                    `/parties/list?unpaidDays=${from}&unpaidDaysMax=${to}`
                                }
                            />
                        )}

                        <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <CardRow
                                label="All parties"
                                value={totalParties}
                                to="/parties/list"
                            />
                        </div>

                        {asOf && (
                            <p className="text-[11px] text-center text-muted-foreground">
                                As of {new Date(asOf).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
